import React, { useState, useEffect } from 'react';
import { Printer, RefreshCw, Settings, FileText, Calculator, AlertCircle, RotateCcw } from 'lucide-react';
import useEscapeToGoBack from '../hooks/useEscapeToGoBack';
import './ReceiptPrinter.css';

// --- CONFIGURATION ---
const DEFAULT_PREFIX = 'B';
const DEFAULT_BASE_URL = 'https://www.lianfoong.com';
const STORAGE_KEY = 'lian-foong-printer-config';

// --- HELPER FUNCTIONS ---

const formatCurrency = (val) => {
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
    minimumFractionDigits: 2
  }).format(val);
};

// Logic: Short codes: MMYY + N  (e.g., 10251 -> B/1025/0001)
const expandReceiptCode = (token, prefix = DEFAULT_PREFIX) => {
  let t = token.replace(/\s/g, '').trim();
  if (!t) return t;

  if (/^[A-Za-z]\/\d{4}\/\d+$/.test(t)) {
    const parts = t.split('/');
    const pref = parts[0].toUpperCase();
    const mmYY = parts[1];
    let seq = parts[2];
    const padLen = pref === 'S' ? 5 : 4;
    seq = seq.padStart(padLen, '0');
    return `${pref}/${mmYY}/${seq}`;
  }

  if (/^[A-Za-z]\d+$/.test(t)) {
    const pref = t.substring(0, 1).toUpperCase();
    const rest = t.substring(1);
    if (rest.length < 5) return t;
    const mmYY = rest.substring(0, 4);
    let seq = rest.substring(4);
    const padLen = pref === 'S' ? 5 : 4;
    seq = seq.padStart(padLen, '0');
    return `${pref}/${mmYY}/${seq}`;
  }

  if (/^\d{9}$/.test(t)) {
    return `S/${t.substring(0, 4)}/${t.substring(4)}`;
  }
  if (/^\d{5,8}$/.test(t)) {
    const mmYY = t.substring(0, 4);
    let seq = t.substring(4);
    seq = seq.padStart(4, '0');
    return `${prefix}/${mmYY}/${seq}`;
  }

  return t;
};

// Parsing amounts
const parseAmountFromText = (text, mode, receiptCode) => {
  const cleanText = text || "";
  const extract = (str) => {
    if (!str) return 0;
    const match = str.replace(/,/g, '').match(/(\d+\.\d{2}|\d+)/);
    return match ? parseFloat(match[0]) : 0;
  };

  if (mode === 'redeem') {
    const totalMatch = cleanText.match(/Total\s*Paid\s*[:=]\s*\$?\s*([\d,.]+)/i);
    if (totalMatch) return extract(totalMatch[1]);
    const pMatch = cleanText.match(/Principal\s*[:=]\s*\$?\s*([\d,.]+)/i);
    const iMatch = cleanText.match(/Interest\s*[:=]\s*\$?\s*([\d,.]+)/i);
    const sum = extract(pMatch?.[1]) + extract(iMatch?.[1]);
    if (sum > 0) return sum;
  } else {
    if (receiptCode) {
      const escapedCode = receiptCode.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const rx = new RegExp(`Receipt\\s+${escapedCode}\\s+\\$?\\s*([\\d,.]+)`, 'i');
      const m = cleanText.match(rx);
      if (m) return extract(m[1]);
    }
    const totalMatch = cleanText.match(/TOTAL\s+\$?\s*([\d,.]+)/i);
    if (totalMatch) return extract(totalMatch[1]);
  }
  const allNumbers = cleanText.match(/(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/g);
  if (allNumbers && allNumbers.length > 0) {
    return extract(allNumbers[allNumbers.length - 1]);
  }
  return 0;
};

// --- COMPONENT ---

export default function ReceiptPrinter() {
  useEscapeToGoBack();

  // State
  const [baseUrl, setBaseUrl] = useState(DEFAULT_BASE_URL);
  const [simulateApi, setSimulateApi] = useState(false);
  const [printerName, setPrinterName] = useState(''); // Selected Printer
  const [availablePrinters, setAvailablePrinters] = useState([]); // List from Electron
  
  // Initial Load
  useEffect(() => {
    let loadedPrinter = '';
    const savedConfig = localStorage.getItem(STORAGE_KEY);
    if (savedConfig) {
      try {
        const parsed = JSON.parse(savedConfig);
        if (parsed.baseUrl) setBaseUrl(parsed.baseUrl);
        if (parsed.simulateApi !== undefined) setSimulateApi(parsed.simulateApi);
        if (parsed.printerName) {
           setPrinterName(parsed.printerName);
           loadedPrinter = parsed.printerName;
        }
      } catch (e) { console.error("Config Load Error", e); }
    }

    // Electron: Get Printers & Auto-Select Epson
    if (window.electronAPI) {
      window.electronAPI.getPrinters().then((printers) => {
        setAvailablePrinters(printers);
        
        if (!loadedPrinter) {
           // 1. Prefer Epson
           const epson = printers.find(p => p.name.toLowerCase().includes('epson'));
           if (epson) {
             setPrinterName(epson.name);
           } else {
             // 2. Fallback to Default
             const def = printers.find(p => p.isDefault);
             if (def) setPrinterName(def.name);
           }
        }
      });
    }
  }, []);

  // Save Config
  useEffect(() => {
    const config = { baseUrl, simulateApi, printerName };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  }, [baseUrl, simulateApi, printerName]);

  const [inputText, setInputText] = useState('');
  const [collectedAmount, setCollectedAmount] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [showSettings, setShowSettings] = useState(false);

  // Computed
  const totalAmount = receipts.reduce((sum, r) => sum + (r.amount || 0), 0);
  const adminFee = receipts.reduce((sum, r) => sum + (r.type === 'renewal' ? 2.0 : 0), 0);
  const collected = parseFloat(collectedAmount) || 0;
  const change = collected > 0 ? collected - adminFee - totalAmount : 0;

  // --- ACTIONS ---

  const handleFetch = async () => {
    const lines = inputText.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (lines.length === 0) { setFetchError("Please enter receipt codes."); return; }

    setIsFetching(true);
    setFetchError(null);
    setReceipts([]);

    const uniqueCodes = [...new Set(lines.map(l => expandReceiptCode(l)))];
    const newReceipts = [];

    try {
      await Promise.all(uniqueCodes.map(async (code) => {
        let amount = 0;
        let type = 'renewal';

        if (simulateApi) {
          await new Promise(r => setTimeout(r, 400));
          type = Math.random() > 0.5 ? 'redeem' : 'renewal';
          amount = Math.floor(Math.random() * 500) + 50; 
        } else {
          try {
            // Check Redeem First
            const redeemUrl = `${baseUrl}/api/printing/redeem-slip?receiptNo=${encodeURIComponent(code)}`;
            const resRedeem = await fetch(redeemUrl, { headers: { 'Accept': 'text/plain' } });
            if (resRedeem.ok) {
              const txt = await resRedeem.text();
              const val = parseAmountFromText(txt, 'redeem', code);
              if (val > 0) { amount = val; type = 'redeem'; }
            }
          } catch (e) { console.warn(`Redeem check failed: ${code}`); }

          // Check Renewal if not found
          if (amount === 0) {
            try {
              const renewalUrl = `${baseUrl}/api/printing/renewal-slip?receipts=${encodeURIComponent(code)}`;
              const resRenewal = await fetch(renewalUrl, { headers: { 'Accept': 'text/plain' } });
              if (resRenewal.ok) {
                const txt = await resRenewal.text();
                const val = parseAmountFromText(txt, 'renewal', code);
                if (val > 0) { amount = val; type = 'renewal'; }
              }
            } catch (e) { console.warn(`Renewal check failed: ${code}`); }
          }
        }
        newReceipts.push({ code, amount, type });
      }));
      setReceipts(newReceipts);
    } catch (err) {
      setFetchError("Failed to fetch. Check API connection.");
    } finally {
      setIsFetching(false);
    }
  };

  const handlePrint = () => {
    if (window.electronAPI) {
      // Electron Silent Print
      window.electronAPI.printSilent(printerName);
    } else {
      // Web Browser Dialog
      window.print();
    }
  };

  const handleClear = () => {
    setInputText('');
    setCollectedAmount('');
    setReceipts([]);
    setFetchError(null);
  };

  const resetConfig = () => {
    setBaseUrl(DEFAULT_BASE_URL);
    setSimulateApi(false);
    setPrinterName('');
    localStorage.removeItem(STORAGE_KEY);
    setShowSettings(false);
  };

  return (
    <div className="rp-container">
      
      {/* Header */}
      <div className="rp-header">
        <div className="rp-header-content">
          <div className="rp-brand">
            <div className="rp-logo-box"><Printer size={20} /></div>
            <h1 className="rp-title">Lian Foong Receipt Printer</h1>
            {window.electronAPI && <span className="rp-badge">Desktop App</span>}
          </div>
          <button 
            onClick={() => setShowSettings(!showSettings)} 
            className={`rp-icon-btn ${showSettings ? 'active' : ''}`}
          >
            <Settings size={20} />
          </button>
        </div>
        
        {/* Settings Panel */}
        {showSettings && (
          <div className="rp-settings-panel">
            <div className="rp-settings-grid">
              <div className="rp-form-group">
                <div className="rp-input-wrapper">
                  <label className="rp-label">API Base URL</label>
                  <input 
                    type="text" 
                    value={baseUrl} 
                    onChange={(e) => setBaseUrl(e.target.value)} 
                    className="rp-input" 
                  />
                </div>
                
                {/* Electron Printer Selector */}
                {window.electronAPI && (
                  <div className="rp-input-wrapper">
                    <label className="rp-label">Target Printer</label>
                    <select 
                      value={printerName} 
                      onChange={(e) => setPrinterName(e.target.value)}
                      className="rp-select"
                    >
                      <option value="">-- Select Printer --</option>
                      {availablePrinters.map((p) => (
                        <option key={p.name} value={p.name}>{p.name} {p.isDefault ? '(Default)' : ''}</option>
                      ))}
                    </select>
                    <p className="rp-hint">Silent printing will target this device.</p>
                  </div>
                )}
              </div>
              
              <div className="rp-settings-actions">
                 <label className="rp-checkbox-wrapper">
                   <input 
                    type="checkbox" 
                    checked={simulateApi} 
                    onChange={(e) => setSimulateApi(e.target.checked)} 
                    className="rp-checkbox" 
                   />
                   <div>
                     <span className="rp-checkbox-label">Simulate API Mode</span>
                     <span className="rp-hint">Enable to test without backend</span>
                   </div>
                 </label>
                 <div className="rp-reset-wrapper">
                    <button onClick={resetConfig} className="rp-link-danger">
                      <RotateCcw size={12} /> Reset to Defaults
                    </button>
                 </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <main className="rp-main">
        
        {/* Left Column */}
        <div className="rp-left-column">
          <div className="rp-card">
            <div className="rp-card-header">
              <h3 className="rp-card-title"><FileText size={18} /> Receipt Entry</h3>
            </div>
            <div className="rp-card-body">
              <div>
                <label className="rp-label">Receipt Numbers</label>
                <textarea 
                  className="rp-textarea" 
                  placeholder="B/1025/0001" 
                  value={inputText} 
                  onChange={(e) => setInputText(e.target.value)} 
                />
              </div>
              <div className="rp-btn-group">
                <button 
                  onClick={handleFetch} 
                  disabled={isFetching || !inputText} 
                  className="rp-btn rp-btn-primary"
                >
                  {isFetching ? <RefreshCw className="rp-spin" size={18}/> : <RefreshCw size={18}/>}
                  {isFetching ? 'Checking...' : 'Get Details'}
                </button>
                <button onClick={handleClear} className="rp-btn rp-btn-secondary">Clear</button>
              </div>
              {fetchError && (
                <div className="rp-alert rp-alert-error">
                  <AlertCircle size={16} /> {fetchError}
                </div>
              )}
            </div>
          </div>

          <div className="rp-card rp-card-padded">
            <h3 className="rp-section-header"><Calculator size={16} /> Settlement</h3>
            <div className="rp-settlement-grid">
              <div className="rp-col-full">
                <label className="rp-label">Collected Amount</label>
                <div className="rp-input-with-icon">
                  <span className="rp-icon-prefix">$</span>
                  <input 
                    type="number" 
                    step="0.01" 
                    className="rp-input-large" 
                    placeholder="0.00" 
                    value={collectedAmount} 
                    onChange={(e) => setCollectedAmount(e.target.value)} 
                  />
                </div>
              </div>
              <div className="rp-summary-box">
                <div className="rp-summary-label">Total Bill</div>
                <div className="rp-summary-value">{formatCurrency(totalAmount)}</div>
              </div>
              <div className="rp-summary-box">
                <div className="rp-summary-label">Admin Fee</div>
                <div className="rp-summary-value">{formatCurrency(adminFee)}</div>
              </div>
              <div className={`rp-change-box ${change < 0 ? 'negative' : 'positive'}`}>
                <span className="rp-change-label">Change Due</span>
                <span className="rp-change-value">{formatCurrency(change)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Receipt Preview */}
        <div className="rp-right-column">
          <div className="rp-preview-header">
            <h2 className="rp-preview-title">Receipt Preview</h2>
            {/* Desktop Print Button (Hidden on Mobile/Zoom) */}
            <button 
              onClick={handlePrint} 
              disabled={receipts.length === 0} 
              className="rp-btn rp-btn-dark rp-desktop-print-btn"
            >
              <Printer size={18} /> Print Slip
            </button>
          </div>

          <div className="receipt-paper">
            <div className="receipt-content">
              <div className="receipt-header">
                <div className="receipt-company">LIAN FOONG PAWNSHOP GROUP</div>
                <div>BLK 214 BEDOK NORTH ST 1</div>
                <div>#01-159  S460214</div>
                <div>UEN: 198602020W</div>
                <div className="receipt-date">DATE: {new Date().toLocaleDateString('en-GB')}</div>
              </div>
              <div className="receipt-divider"></div>
              
              <div className="receipt-items">
                {receipts.length === 0 && <div className="receipt-empty">(No receipts loaded)</div>}
                {receipts.map((r, idx) => (
                  <div key={idx} className="receipt-row">
                    <div className="receipt-row-left">
                       <span className="receipt-type">{r.type}</span>
                       <span>{r.code}</span>
                    </div>
                    <div className="receipt-row-right">{formatCurrency(r.amount).replace('SGD', '')}</div>
                  </div>
                ))}
              </div>

              <div className="receipt-divider"></div>
              <div className="receipt-totals">
                <div className="receipt-total-row"><span>TOTAL:</span><span>{formatCurrency(totalAmount)}</span></div>
                <div className="receipt-total-row"><span>COLLECT:</span><span>{formatCurrency(collected)}</span></div>
                {adminFee > 0 && <div className="receipt-total-row"><span>ADMIN FEE:</span><span>{formatCurrency(adminFee)}</span></div>}
                <div className="receipt-final-change"><span>CHANGE:</span><span>{formatCurrency(change)}</span></div>
              </div>
              <div className="receipt-footer">*** THANK YOU ***</div>
            </div>
          </div>
          <div className="rp-printer-hint"><p>Select your Thermal Printer (80mm) in the settings above.</p></div>
        </div>
      </main>

      {/* Mobile / Zoomed-in Print Bar */}
      <div className="rp-mobile-action-bar">
        <div className="rp-mobile-summary">
          <span className="rp-mobile-label">Total</span>
          <span className="rp-mobile-value">{formatCurrency(totalAmount)}</span>
        </div>
        <button 
          onClick={handlePrint} 
          disabled={receipts.length === 0} 
          className="rp-btn rp-btn-dark rp-btn-full"
        >
          <Printer size={18} /> Print
        </button>
      </div>

    </div>
  );
}